import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Smartphone, Shuffle, RefreshCw, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SendMessage({ user }) {
  const [number, setNumber] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  
  // Multi-session state
  const [connectedDevices, setConnectedDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState('');
  const [routingEnabled, setRoutingEnabled] = useState(false);
  const [devicesLoading, setDevicesLoading] = useState(true);

  // Fetch connected devices
  const fetchConnectedDevices = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/whatsapp/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      // Filter only connected sessions with phone numbers
      const connected = (data.sessions || []).filter(
        s => s.status === 'connected' && s.phone_number
      );
      setConnectedDevices(connected);
      
      // Auto-select first device if none selected
      if (connected.length > 0 && !selectedDevice) {
        setSelectedDevice(connected[0].session_id);
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
    } finally {
      setDevicesLoading(false);
    }
  }, [selectedDevice]);

  useEffect(() => {
    fetchConnectedDevices();
    // Refresh every 10 seconds
    const interval = setInterval(fetchConnectedDevices, 10000);
    return () => clearInterval(interval);
  }, [fetchConnectedDevices]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);

    // Validation: No devices connected
    if (connectedDevices.length === 0) {
      setError('No WhatsApp device connected. Please connect first from WhatsApp Connections page.');
      setLoading(false);
      return;
    }

    // Validation: If routing is disabled, session_id is required
    if (!routingEnabled && !selectedDevice) {
      setError('Please select a device or enable Random Device Routing.');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }
      
      // Build request payload with multi-session params
      const payload = { 
        number, 
        message,
        routing: routingEnabled
      };
      
      // Add session_id only if routing is disabled
      if (!routingEnabled) {
        payload.session_id = selectedDevice;
      }
      
      const response = await axios.post(
        `${API}/messages/send`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(response.data);
      setNumber('');
      setMessage('');
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      } else {
        setError(err.response?.data?.detail || 'Failed to send message');
      }
    } finally {
      setLoading(false);
    }
  };

  // Format phone number for display
  const formatPhone = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.startsWith('91') && cleaned.length === 12) {
      return `+91 ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
    }
    return phone;
  };

  return (
    <Layout user={user}>
      <div className="page-header">
        <h1 data-testid="page-title">Send Message</h1>
        <p>Send WhatsApp messages directly from the dashboard</p>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>New Message</h3>
        </div>

        {error && (
          <div className="alert alert-error" data-testid="error-message">
            {error}
          </div>
        )}

        {result && (
          <div className="alert alert-success" data-testid="success-message">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <CheckCircle size={18} style={{ color: '#16a34a' }} />
              <strong>Message sent successfully!</strong>
            </div>
            <div style={{ fontSize: '13px', color: '#166534', paddingLeft: '26px' }}>
              <div>To: {result.to}</div>
              {result.sender_number && <div>From: {formatPhone(result.sender_number)}</div>}
              {result.session_id && <div>Session: {result.session_id}</div>}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Device Selection Section */}
          <div style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Smartphone size={18} style={{ color: '#667eea' }} />
                <span style={{ fontWeight: '600', color: '#334155' }}>Send From Device</span>
              </div>
              {devicesLoading && <RefreshCw size={14} className="animate-spin" style={{ color: '#64748b' }} />}
            </div>

            {/* Connected Devices Dropdown */}
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <select
                value={selectedDevice}
                onChange={(e) => setSelectedDevice(e.target.value)}
                disabled={routingEnabled || connectedDevices.length === 0}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: routingEnabled ? '#f1f5f9' : 'white',
                  cursor: routingEnabled ? 'not-allowed' : 'pointer',
                  color: routingEnabled ? '#94a3b8' : '#334155'
                }}
                data-testid="device-select"
              >
                {connectedDevices.length === 0 ? (
                  <option value="">No devices connected</option>
                ) : (
                  connectedDevices.map((device) => (
                    <option key={device.session_id} value={device.session_id}>
                      {formatPhone(device.phone_number)} ({device.session_id})
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* Routing Toggle */}
            {connectedDevices.length > 1 && (
              <div 
                onClick={() => setRoutingEnabled(!routingEnabled)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 12px',
                  background: routingEnabled ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
                  border: routingEnabled ? 'none' : '1px solid #e2e8f0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                data-testid="routing-toggle"
              >
                <Shuffle size={16} style={{ color: routingEnabled ? 'white' : '#667eea' }} />
                <div style={{ flex: 1 }}>
                  <p style={{ 
                    margin: 0, 
                    fontWeight: '600', 
                    fontSize: '13px',
                    color: routingEnabled ? 'white' : '#334155'
                  }}>
                    Random Device Routing
                  </p>
                  <p style={{ 
                    margin: '2px 0 0', 
                    fontSize: '11px',
                    color: routingEnabled ? 'rgba(255,255,255,0.8)' : '#64748b'
                  }}>
                    Distribute messages across all {connectedDevices.length} connected devices
                  </p>
                </div>
                <div style={{
                  width: '40px',
                  height: '22px',
                  background: routingEnabled ? 'rgba(255,255,255,0.3)' : '#e2e8f0',
                  borderRadius: '11px',
                  position: 'relative',
                  transition: 'all 0.2s ease'
                }}>
                  <div style={{
                    width: '18px',
                    height: '18px',
                    background: 'white',
                    borderRadius: '50%',
                    position: 'absolute',
                    top: '2px',
                    left: routingEnabled ? '20px' : '2px',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                  }} />
                </div>
              </div>
            )}

            {/* Status Indicators */}
            <div style={{ marginTop: '12px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {connectedDevices.length > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <CheckCircle size={14} style={{ color: '#22c55e' }} />
                  <span style={{ fontSize: '12px', color: '#22c55e', fontWeight: '500' }}>
                    {connectedDevices.length} device{connectedDevices.length > 1 ? 's' : ''} connected
                  </span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <XCircle size={14} style={{ color: '#ef4444' }} />
                  <span style={{ fontSize: '12px', color: '#ef4444', fontWeight: '500' }}>
                    No devices connected
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="number">Mobile Number</label>
            <input
              id="number"
              type="text"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              placeholder="Enter number (e.g., 9876543210 or +919876543210)"
              required
              data-testid="number-input"
            />
            <small style={{ color: '#64748b', fontSize: '13px', marginTop: '4px', display: 'block' }}>
              Enter with or without country code. Default: +91
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message here..."
              required
              rows="6"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '15px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
              data-testid="message-input"
            />
          </div>

          <button
            type="submit"
            className="btn-primary"
            disabled={loading || connectedDevices.length === 0}
            data-testid="send-button"
            style={{
              opacity: connectedDevices.length === 0 ? 0.6 : 1,
              cursor: connectedDevices.length === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Sending...' : connectedDevices.length === 0 ? 'Connect Device First' : 'Send Message'}
          </button>
        </form>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>API Usage Example</h3>
        </div>
        <div className="alert alert-info">
          <p style={{ marginBottom: '12px', fontWeight: '600' }}>You can also send messages via API:</p>
          <pre style={{ background: 'white', padding: '16px', borderRadius: '8px', overflow: 'auto', fontSize: '13px' }}>
{`GET ${BACKEND_URL}/api/send?api_key=YOUR_API_KEY&session_id=session_0&number=9876543210&msg=Hello&routing=false`}
          </pre>
          <div style={{ marginTop: '16px', fontSize: '13px' }}>
            <p style={{ fontWeight: '600', marginBottom: '8px' }}>Parameters:</p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <tbody>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '6px 8px', fontWeight: '500' }}>api_key</td>
                  <td style={{ padding: '6px 8px', color: '#dc2626' }}>Required</td>
                  <td style={{ padding: '6px 8px' }}>Your API key from API Keys page</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '6px 8px', fontWeight: '500' }}>number</td>
                  <td style={{ padding: '6px 8px', color: '#dc2626' }}>Required</td>
                  <td style={{ padding: '6px 8px' }}>Recipient number (with or without +91)</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '6px 8px', fontWeight: '500' }}>msg</td>
                  <td style={{ padding: '6px 8px', color: '#dc2626' }}>Required</td>
                  <td style={{ padding: '6px 8px' }}>Message text (URL encoded)</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '6px 8px', fontWeight: '500' }}>session_id</td>
                  <td style={{ padding: '6px 8px', color: '#f59e0b' }}>Conditional</td>
                  <td style={{ padding: '6px 8px' }}>Device session (e.g., session_0, session_1)</td>
                </tr>
                <tr>
                  <td style={{ padding: '6px 8px', fontWeight: '500' }}>routing</td>
                  <td style={{ padding: '6px 8px', color: '#f59e0b' }}>Conditional</td>
                  <td style={{ padding: '6px 8px' }}>true = random device, false = use session_id</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: '12px', fontSize: '13px', background: '#fef3c7', padding: '10px 12px', borderRadius: '6px', color: '#92400e' }}>
            <strong>⚠️ Note:</strong> Either <code>session_id</code> OR <code>routing=true</code> is required. Without these, API will return error.
          </div>
        </div>
      </div>
    </Layout>
  );
}
